
import React, { useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Calendar,
  Clock,
  ChevronRight,
  ArrowLeft,
  Users,
  ChevronLeft,
} from 'lucide-react';
import { useBlog } from './BlogBackend';

interface PostNavigationProps {
  prevPost?: any;
  nextPost?: any;
  onNavigate: (post: any) => void;
}

const PostNavigation: React.FC<PostNavigationProps> = ({
  prevPost,
  nextPost,
  onNavigate,
}) => {
  if (!prevPost && !nextPost) return null;

  return (
    <div className="mt-12 pt-8 border-t border-gray-200">
      {/* Mobile Layout */}
      <div className="block md:hidden space-y-4">
        {/* Previous Post */}
        {prevPost ? (
          <button
            onClick={() => onNavigate(prevPost)}
            className="group flex items-center space-x-3 text-left w-full p-4 rounded-lg hover:bg-gray-50 transition-colors duration-200 border border-gray-200"
          >
            <div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-full group-hover:bg-[#27a887] group-hover:text-white transition-colors duration-200 flex-shrink-0">
              <ChevronLeft size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-500 mb-1">Previous Post</p>
              <h3 className="text-base font-medium text-gray-900 group-hover:text-[#27a887] transition-colors duration-200 line-clamp-2">
                {prevPost.title}
              </h3>
            </div>
          </button>
        ) : (
          <div className="opacity-50 cursor-not-allowed p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-full flex-shrink-0">
                <ChevronLeft size={20} className="text-gray-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-400 mb-1">No Previous Post</p>
              </div>
            </div>
          </div>
        )}

        {/* Next Post */}
        {nextPost ? (
          <button
            onClick={() => onNavigate(nextPost)}
            className="group flex items-center space-x-3 text-left w-full p-4 rounded-lg hover:bg-gray-50 transition-colors duration-200 border border-gray-200"
          >
            <div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-full group-hover:bg-[#27a887] group-hover:text-white transition-colors duration-200 flex-shrink-0">
              <ChevronRight size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-500 mb-1">Next Post</p>
              <h3 className="text-base font-medium text-gray-900 group-hover:text-[#27a887] transition-colors duration-200 line-clamp-2">
                {nextPost.title}
              </h3>
            </div>
          </button>
        ) : (
          <div className="opacity-50 cursor-not-allowed p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-full flex-shrink-0">
                <ChevronRight size={20} className="text-gray-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-400 mb-1">No Next Post</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Desktop Layout */}
      <div className="hidden md:flex justify-between items-start">
        {/* Previous Post */}
        <div className="flex-1 mr-4">
          {prevPost ? (
            <button
              onClick={() => onNavigate(prevPost)}
              className="group flex items-start space-x-3 text-left w-full p-4 rounded-lg hover:bg-gray-50 transition-colors duration-200"
            >
              <div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-full group-hover:bg-[#27a887] group-hover:text-white transition-colors duration-200">
                <ChevronLeft size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-500 mb-1">Previous Post</p>
                <h3 className="text-lg font-medium text-gray-900 group-hover:text-[#27a887] transition-colors duration-200 line-clamp-2">
                  {prevPost.title}
                </h3>
              </div>
            </button>
          ) : (
            <div className="opacity-50 cursor-not-allowed">
              <div className="flex items-start space-x-3 p-4">
                <div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-full">
                  <ChevronLeft size={20} className="text-gray-400" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-400 mb-1">No Previous Post</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Next Post */}
        <div className="flex-1 ml-4">
          {nextPost ? (
            <button
              onClick={() => onNavigate(nextPost)}
              className="group flex items-start space-x-3 text-right w-full p-4 rounded-lg hover:bg-gray-50 transition-colors duration-200"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-500 mb-1">Next Post</p>
                <h3 className="text-lg font-medium text-gray-900 group-hover:text-[#27a887] transition-colors duration-200 line-clamp-2">
                  {nextPost.title}
                </h3>
              </div>
              <div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-full group-hover:bg-[#27a887] group-hover:text-white transition-colors duration-200">
                <ChevronRight size={20} />
              </div>
            </button>
          ) : (
            <div className="opacity-50 cursor-not-allowed">
              <div className="flex items-start space-x-3 p-4">
                <div className="flex-1">
                  <p className="text-sm text-gray-400 mb-1">No Next Post</p>
                </div>
                <div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-full">
                  <ChevronRight size={20} className="text-gray-400" />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Skeleton Loading Component
const BlogSkeleton: React.FC = () => {
  return (
    <div className="max-w-3xl mx-auto px-4 pt-25 pb-16">
      {/* Breadcrumbs Skeleton */}
      <div className="mb-6 flex items-center space-x-2">
        <div className="h-4 w-10 bg-gray-200 rounded animate-pulse"></div>
        <div className="text-gray-300">/</div>
        <div className="h-4 w-8 bg-gray-200 rounded animate-pulse"></div>
        <div className="text-gray-300">/</div>
        <div className="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
      </div>

      {/* Back Button Skeleton */}
      <div className="mb-6 flex items-center">
        <div className="h-4 w-4 bg-gray-200 rounded mr-2 animate-pulse"></div>
        <div className="h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
      </div>

      {/* Featured Image Skeleton */}
      <div className="w-full h-64 bg-gray-200 rounded-lg mb-6 animate-pulse"></div>

      {/* Title Skeleton */}
      <div className="mb-4 space-y-2">
        <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
        <div className="h-8 w-3/4 bg-gray-200 rounded animate-pulse"></div>
      </div>

      {/* Meta Information Skeleton */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="flex items-center">
          <div className="h-3.5 w-3.5 bg-gray-200 rounded mr-1 animate-pulse"></div>
          <div className="h-4 w-16 bg-gray-200 rounded animate-pulse"></div>
        </div>
        <div className="flex items-center">
          <div className="h-3.5 w-3.5 bg-gray-200 rounded mr-1 animate-pulse"></div>
          <div className="h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
        </div>
        <div className="flex items-center">
          <div className="h-3.5 w-3.5 bg-gray-200 rounded mr-1 animate-pulse"></div>
          <div className="h-4 w-16 bg-gray-200 rounded animate-pulse"></div>
        </div>
        <div className="h-6 w-16 bg-gray-200 rounded-full animate-pulse"></div>
      </div>

      {/* Content Skeleton */}
      <div className="prose prose-lg max-w-full space-y-4">
        {/* Paragraph lines */}
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="space-y-2">
            <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-4 w-5/6 bg-gray-200 rounded animate-pulse"></div>
          </div>
        ))}
        
        {/* Subheading */}
        <div className="h-6 w-1/2 bg-gray-200 rounded animate-pulse mt-8 mb-4"></div>
        
        {/* More paragraph lines */}
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i + 8} className="space-y-2">
            <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-4 w-4/5 bg-gray-200 rounded animate-pulse"></div>
          </div>
        ))}
      </div>

      {/* Navigation Skeleton */}
      <div className="mt-12 pt-8 border-t border-gray-200">
        <div className="flex justify-between items-start">
          {/* Previous Post Skeleton */}
          <div className="flex-1 mr-4">
            <div className="flex items-start space-x-3 p-4">
              <div className="w-10 h-10 bg-gray-200 rounded-full animate-pulse"></div>
              <div className="flex-1 space-y-2">
                <div className="h-3 w-20 bg-gray-200 rounded animate-pulse"></div>
                <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse"></div>
              </div>
            </div>
          </div>

          {/* Next Post Skeleton */}
          <div className="flex-1 ml-4">
            <div className="flex items-start space-x-3 p-4">
              <div className="flex-1 space-y-2">
                <div className="h-3 w-20 bg-gray-200 rounded animate-pulse ml-auto"></div>
                <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse ml-auto"></div>
              </div>
              <div className="w-10 h-10 bg-gray-200 rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const BlogView: React.FC = () => {
  // Capture params: category = tag slug, slug = post slug
  const { category, slug } = useParams<{ category: string; slug: string }>();
  const navigate = useNavigate();
  const blog = useBlog({ slug, navigate });

  const articleTopRef = useRef<HTMLDivElement | null>(null);

  // Scroll to top whenever a new post loads
  useEffect(() => {
    if (blog.selectedPost) {
      window.scrollTo({ top: 0 });
    }
  }, [blog.selectedPost]);

  const handleBackWithAnimation = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      blog.handleBack();
    }, 400);
  };

  // Compute prev/next posts
  const getAdjacentPosts = () => {
    if (!blog.selectedPost || !blog.posts.length) {
      return { prevPost: null, nextPost: null };
    }
    const currentIndex = blog.posts.findIndex(
      (post) => post.id === blog.selectedPost?.id
    );
    if (currentIndex === -1) {
      return { prevPost: null, nextPost: null };
    }
    const prevPost = currentIndex > 0 ? blog.posts[currentIndex - 1] : null;
    const nextPost =
      currentIndex < blog.posts.length - 1
        ? blog.posts[currentIndex + 1]
        : null;
    return { prevPost, nextPost };
  };

  const handlePostNavigation = (post: any) => {
    const tagSlug =
      post.primary_tag?.slug ||
      post.tags[0]?.slug ||
      'uncategorized';
    navigate(`/blog/${tagSlug}/${post.slug}`);
  };

  // If post is still loading, show skeleton
  if (!blog.selectedPost) {
    return <BlogSkeleton />;
  }

  // Now we have the post
  const post = blog.selectedPost;
  const { prevPost, nextPost } = getAdjacentPosts();
  const canonicalUrl = `${window.location.origin}/blog/${category}/${post.slug}`;

  return (
    <div ref={articleTopRef} className="pt-25 pb-16">
      {/* -------------------------------------------------------------------
          SEO Meta Tags for the single‐post view
      ------------------------------------------------------------------- */}
      <Helmet>
        <title>{blog.getMetaTitle(post)}</title>
        <meta name="description" content={blog.getMetaDescription(post)} />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta
          property="og:title"
          content={post.og_title || blog.getMetaTitle(post)}
        />
        <meta
          property="og:description"
          content={post.og_description || blog.getMetaDescription(post)}
        />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        {blog.getMetaImage(post) && (
          <meta property="og:image" content={blog.getMetaImage(post)} />
        )}
        <meta property="og:site_name" content="Eximpe Blog" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={post.twitter_title || blog.getMetaTitle(post)}
        />
        <meta
          name="twitter:description"
          content={post.twitter_description || blog.getMetaDescription(post)}
        />
        {(post.twitter_image || blog.getMetaImage(post)) && (
          <meta
            name="twitter:image"
            content={post.twitter_image || blog.getMetaImage(post)!}
          />
        )}

        {/* Article‐specific */}
        <meta property="article:published_time" content={post.published_at} />
        <meta property="article:modified_time" content={post.updated_at} />
        {post.authors?.map((author) => (
          <meta key={author.id} property="article:author" content={author.name} />
        ))}
        {post.tags?.map((tag) => (
          <meta key={tag.id} property="article:tag" content={tag.name} />
        ))}

        {/* JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: blog.getMetaTitle(post),
            description: blog.getMetaDescription(post),
            image: blog.getMetaImage(post),
            author: {
              "@type": "Person",
              name:
                post.authors?.map((a) => a.name).join(', ') || 'Eximpe Team',
            },
            publisher: {
              "@type": "Organization",
              name: 'Eximpe',
              logo: {
                "@type": 'ImageObject',
                url: `${window.location.origin}/logo.png`,
              },
            },
            datePublished: post.published_at,
            dateModified: post.updated_at,
            mainEntityOfPage: {
              "@type": 'WebPage',
              "@id": canonicalUrl,
            },
          })}
        </script>
      </Helmet>

      <div className="max-w-3xl mx-auto px-4">
        {/* Breadcrumbs */}
        <nav aria-label="breadcrumb" className="mb-6">
          <ol className="flex items-center text-sm text-gray-500 space-x-2">
            <li>
              <Link to="/" className="hover:underline">
                Home
              </Link>
            </li>
            <li>/</li>
            <li>
              <Link to="/blog" className="hover:underline">
                Blog
              </Link>
            </li>
            <li>/</li>
            <li className="text-gray-700">{post.title}</li>
          </ol>
        </nav>

        {/* Back to All Posts Button */}
        <button
          onClick={handleBackWithAnimation}
          className="mb-6 flex items-center text-[#27a887] hover:cursor-pointer"
        >
          <ArrowLeft size={16} className="mr-2" />
          Back to posts
        </button>

        <article>
          {/* Featured Image */}
          {blog.getFeaturedImageUrl(post) && (
            <img
              src={blog.getFeaturedImageUrl(post)!}
              alt={post.title}
              className="w-full mb-6 rounded-lg"
            />
          )}

          {/* Title */}
          <h1 className="text-3xl font-bold mb-4">{post.title}</h1>

          {/* Meta (authors, date, reading time, tag) */}
          <div className="flex flex-wrap items-center text-gray-500 mb-6 gap-4 text-sm">
            {post.authors?.length > 0 && (
              <div className="flex items-center">
                <Users size={14} className="mr-1" />
                {post.authors.map((a) => a.name).join(', ')}
              </div>
            )}
            <div className="flex items-center">
              <Calendar size={14} className="mr-1" />
              {blog.formatDate(post.published_at)}
            </div>
            <div className="flex items-center">
              <Clock size={14} className="mr-1" />
              {post.reading_time || blog.calcReadTime(post.html)} min read
            </div>
            {post.primary_tag && (
              <span className="bg-[#e6fbf8] text-[#27a887] px-2 py-0.5 rounded-full text-xs">
                {post.primary_tag.name}
              </span>
            )}
          </div>

          {/* Post Content */}
          <div
            className="prose prose-lg max-w-full"
            dangerouslySetInnerHTML={{ __html: post.html }}
          />

          {/* Prev/Next Navigation */}
          <PostNavigation
            prevPost={prevPost}
            nextPost={nextPost}
            onNavigate={handlePostNavigation}
          />
        </article>
      </div>
    </div>
  );
};

export default BlogView;